/** Bluff Off. Keep the original module/socket IDs compatible with saved rooms. */
export const BLUFF_MODULE_ID = 'own-it-or-bluff';
export const BLUFF_NAME = 'Bluff Off';
export const BLUFF_SETS_TO_WIN = 2;
export const BLUFF_COPIES_PER_SET = 4;
export const BLUFF_EVENTS = {
  ACTION: 'bluff:action',
  SNAPSHOT: 'bluff:snapshot',
  STATE: 'bluff:state',
  HAND: 'bluff:hand',
  HOST: 'bluff:host',
  ERROR: 'bluff:error',
  LEAVE: 'bluff:leave',
} as const;
export type BluffRating = 'mild' | 'wild' | 'unhinged';
/** Legacy saved clock fields remain readable; chooseSeconds now controls a soft reminder only. */
export interface BluffTiming {
  chooseSeconds: number;
  challengeSeconds: number;
  revealSeconds: number;
}
export function isBluffTiming(value: unknown): value is BluffTiming {
  if (!value || typeof value !== 'object') return false;
  return Object.entries({
    chooseSeconds: [10, 120],
    challengeSeconds: [5, 60],
    revealSeconds: [3, 30],
  }).every(([key, [min, max]]) => {
    const seconds = (value as Record<string, unknown>)[key];
    return (
      typeof seconds === 'number' && Number.isInteger(seconds) && seconds >= min && seconds <= max
    );
  });
}
export const BLUFF_PACE_OPTIONS = [
  {
    id: 'quick',
    label: 'Quick',
    description: 'Gentle reminder after 30s',
    submitMs: 30_000,
    challengeMs: 15_000,
    revealMs: 8_000,
  },
  {
    id: 'standard',
    label: 'Standard',
    description: 'Gentle reminder after 45s',
    submitMs: 45_000,
    challengeMs: 20_000,
    revealMs: 10_000,
  },
  {
    id: 'relaxed',
    label: 'Relaxed',
    description: 'Gentle reminder after 60s',
    submitMs: 60_000,
    challengeMs: 30_000,
    revealMs: 15_000,
  },
] as const;
export type BluffPace = (typeof BLUFF_PACE_OPTIONS)[number]['id'];
export const DEFAULT_BLUFF_PACE: BluffPace = 'standard';
export function getBluffPace(pace: BluffPace) {
  return BLUFF_PACE_OPTIONS.find(option => option.id === pace)!;
}
export function getBluffTiming(pace: BluffPace): BluffTiming {
  const option = getBluffPace(pace);
  return {
    chooseSeconds: option.submitMs / 1000,
    challengeSeconds: option.challengeMs / 1000,
    revealSeconds: option.revealMs / 1000,
  };
}
export const DEFAULT_BLUFF_TIMING = getBluffTiming(DEFAULT_BLUFF_PACE);
export function resolveBluffPace(setup: unknown): BluffPace {
  const data = setup as { pace?: unknown; timing?: unknown } | null;
  if (!data || typeof data !== 'object') return DEFAULT_BLUFF_PACE;
  const explicit = BLUFF_PACE_OPTIONS.find(option => option.id === data.pace);
  if (explicit) return explicit.id;
  const timing = data.timing;
  if (!isBluffTiming(timing)) return DEFAULT_BLUFF_PACE;
  return (
    BLUFF_PACE_OPTIONS.find(
      option =>
        option.submitMs === timing.chooseSeconds * 1000 &&
        option.challengeMs === timing.challengeSeconds * 1000 &&
        option.revealMs === timing.revealSeconds * 1000
    )?.id ?? DEFAULT_BLUFF_PACE
  );
}
export const BLUFF_PACKS = [
  { id: 'classic', name: 'Classic Chaos', description: 'Everyday disasters and social regrets.' },
  { id: 'boomers', name: 'Boomers', description: 'Reply-all legends and technology meltdowns.' },
  {
    id: 'jesus-christ',
    name: 'Jesus Christ',
    description: 'Church awkwardness and biblical-scale mishaps.',
  },
  {
    id: 'mom-and-dad',
    name: 'Mom and Dad',
    description: 'Parenting fails and family oversharing.',
  },
  {
    id: 'america',
    name: 'America',
    description: 'Cookouts, drive-throughs, and patriotic excess.',
  },
] as const;
export type BluffPackId = (typeof BLUFF_PACKS)[number]['id'];

export interface BluffSituation {
  /** A unique physical copy. Matching uses familyId, never wording analysis. */
  id: string;
  familyId: string;
  text: string;
  symbol: string;
  packId: BluffPackId;
}
export interface BluffReceipt {
  card: BluffSituation;
  round: number;
}
export interface BluffPlayer {
  id: string;
  name: string;
  connected: boolean;
  score: number;
  handCount: number;
  receipts: BluffReceipt[];
}
export interface BluffRequest {
  askerId: string;
  targetId: string;
  card: BluffSituation;
}
export interface BluffOutcome {
  kind: 'given' | 'believed' | 'caught' | 'wrong-call' | 'skipped';
  fromId: string | null;
  toId: string | null;
  cardCount: number;
}
export interface BluffState {
  roomId: string;
  gameId: string;
  revision: number;
  phase:
    | 'lobby'
    | 'preparing'
    | 'intro'
    | 'tutorial'
    | 'asking'
    | 'responding'
    | 'challenging'
    | 'reveal'
    | 'finished';
  hostId: string;
  rating: BluffRating;
  pace: BluffPace;
  timing: BluffTiming;
  tutorialStep: 0 | 1;
  packIds: BluffPackId[];
  players: BluffPlayer[];
  waitingPlayerIds?: string[];
  /** Monotonic turn number, retained as round on the existing transport. */
  round: number;
  setsToWin: number;
  deckCount: number;
  currentPlayerId: string | null;
  request: BluffRequest | null;
  outcome: BluffOutcome | null;
  /** Intro guard or one-shot reminder. A reminder NEVER resolves a player's move. */
  deadline: number | null;
  nudged: boolean;
  serverNow: number;
  banter: { playerId: string; text: string }[];
  leaderboard: BluffPlayer[];
  winnerIds: string[];
  paused: boolean;
  lastEvent: string;
}
export interface BluffHand {
  roomId: string;
  gameId: string;
  round: number;
  revision: number;
  cards: BluffSituation[];
}
export type BluffAction = { roomId: string; gameId: string; round: number } & (
  | { type: 'start' | 'skip-intro' | 'tutorial-next' | 'next-turn' | 'skip-turn' | 'end' }
  | {
      type: 'configure';
      rating: BluffRating;
      packIds: BluffPackId[];
      pace?: BluffPace;
      timing?: BluffTiming;
    }
  | { type: 'ask'; cardId: string; targetId: string }
  | { type: 'respond'; give: boolean }
  | { type: 'decide'; challenge: boolean }
  | { type: 'banter'; text: string; messageId: string }
);
